import { useState, useEffect, useRef } from 'react';
import { toCalendarDay, fromCalendarDay } from '../../utils/dateFormatter';
import { useParams, useNavigate } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers';
import { useDropzone } from 'react-dropzone';
import { styled } from '@mui/material/styles';
import PageTabs from '../shared/PageTabs';
import {
    DndContext,
    useSensor,
    useSensors,
    PointerSensor,
    KeyboardSensor
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    arrayMove
} from '@dnd-kit/sortable';
import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    Typography,
    Button,
    IconButton,
    Grid,
    Autocomplete,
    TextField,
    FormHelperText,
    Snackbar,
    Alert,
    Tab,
    Tabs,
    Card,
    CardContent,
    Stack
} from '@mui/material';
import {
    Upload as UploadIcon,
    Link as LinkIcon,
    Delete as DeleteIcon,
    Clear as ClearIcon
} from '@mui/icons-material';
import { learningPathService } from '../../services/api';
import { LPathSortablePrerequisite } from './LPathSortablePrerequisite';
import { useToast } from '../../context/ToastContext';
import { useUser } from '../../context/UserContext';
import { buildLPathTabs } from '../shared/tabConfigs';
const DropzoneArea = styled(Box)(({ theme })=> ({
  border: `2px dashed ${theme.palette.grey[300]}`,
  borderRadius: '12px',
  padding: theme.spacing(4),
  backgroundColor: theme.palette.grey[50],
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.grey[100]
  }
}));

const LPathSettings = () => {
    const { id } = useParams();

    const { user } = useUser();
    const permissions = user?.permissions || [];

    const lpathTabs = buildLPathTabs(id, { permissions: user?.permissions || [], roleId: user?.role_id });

    const navigate = useNavigate();
    const { showToast } = useToast();
    
    const initialized = useRef(false);
    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const [lpaths, setLPaths] = useState([]);
    const [tabValue, setTabValue] = useState(0);
    const [deletedAttachmentIds, setDeletedAttachmentIds] = useState([]);

    const [settings, setSettings] = useState({
        enrollment_type: 2,
        self_unenrollment: false,
        auto_retire: null,
        prerequisites: [],
        status: 1,
        //mandatory_lpath: false,
        //lpath_end_date: null,
        authors: [],
        attachments: [],
        resourse_type: 'url',
        resourse_url: ''
    });
    const [authorOptions, setAuthorOptions] = useState([]);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });
    const [authorSearch, setAuthorSearch] = useState("");
    useEffect(() => {
        if (initialized.current) return;
        initialized.current = true;

        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await learningPathService.getSettings(id);
                const { data } = response.data;
                const nameParts = user.name ? user.name.split(" ") : ["", ""];
                const firstName = nameParts[0] || "";
                const lastName = nameParts.slice(1).join(" ") || "";
                const currentUser = {
                  id: user.id,
                  first_name: firstName,
                  last_name: lastName,
                };
                const selectedAuthors = data.selectedAuthors?.map((author) => ({
                  id: author.id,
                  first_name: author.first_name,
                  last_name: author.last_name,
                })) || [];

                const currentUserExists = selectedAuthors.some(
                  (author) => author.id === currentUser.id
                );
                const finalAuthors = currentUserExists
                  ? selectedAuthors
                  : [currentUser, ...selectedAuthors];

                setSettings({
                    enrollment_type: parseInt(data.enrollment_type) || 2,
                    self_unenrollment: data.self_unenrollment === true || data.self_unenrollment === '1',
                    // The date comes and goes as a calendar day (S-19 (f) sub-point). Removed 17 Oct 2026 (S-19 (f)):
                    // reading it as a UTC time and sending toISOString's day, so it moved a day east or west of UTC.
                    auto_retire: fromCalendarDay(data.auto_retire),
                    prerequisites: [],
                    // prerequisites: data.prerequisites?.map(p => ({
                    //     oid: p.lpp_id,
                    //     id: p.cp_id,
                    //     name: p.title,
                    //     prerequisite_type: p.prerequisite_type,
                    //     required: p.required === '1'
                    // })) || [],
                    status: parseInt(data.lpath_info?.status) || 1,
                    //mandatory_lpath: data.lpath_info?.mandatory_lpath === '1' || data.lpath_info?.mandatory_lpath === 1 || data.lpath_info?.mandatory_lpath === true,
                    //lpath_end_date: data.lpath_info?.lpath_end_date ? new Date(data.lpath_info.lpath_end_date) : null,
                    authors: finalAuthors,
                    attachments: data.attachments || [],
                    resourse_type: data.resourse_type || 'url',
                    resourse_url: ""
                });
            } catch (error) {
                console.error('Error fetching settings:', error);
                showToast('Error loading settings', 'error');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id, showToast, user]);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    );

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        if (!active || !over || active.id === over.id) return;

        setSettings((prev) => {
            const oldIndex = prev.prerequisites.findIndex(p => String(p.oid) === String(active.id));
            const newIndex = prev.prerequisites.findIndex(p => String(p.oid) === String(over.id));
            if (oldIndex === -1 || newIndex === -1) return prev; // fail safe

            const newOrder = arrayMove(prev.prerequisites, oldIndex, newIndex);

            (async () => {
                try {
                    const formData = {
                        prerequisites: newOrder.map(p => p.oid),
                        prerequisites_mandatory: newOrder.filter(p => p.required === '1' || p.required === 1).map(p => p.oid),
                    };
                    await learningPathService.updatePrerequisitesOrdering(formData);
                } catch (err) {
                    console.error("Failed to update prerequisite order:", err);
                }
            })();

            return { ...prev, prerequisites: newOrder };
        });
    };

    const handleRemovePrerequisite = (id) => {
        // prerequisites removed from learning path settings UI
        return;
    };

    const handleSubmit = async (submitAction) => {
        const newErrors = {};
        // VALIDATE DATES BEFORE ANYTHING ELSE
        /*
        if (settings.lpath_end_date && settings.auto_retire) {
            if (settings.lpath_end_date > settings.auto_retire) {
                newErrors.lpath_end_date = "End Date must be less than or equal to Auto Retire Date";
                setErrors(newErrors);
                setSnackbar({
                open: true,
                message: "End Date must be less than or equal to Auto Retire Date",
                severity: "error",
                });
                return;
            }
        }
        */

        if (settings.authors.length === 0) {
            newErrors.authors = "At least one author is required";
        }

        setErrors(newErrors);

        if (Object.keys(newErrors).length > 0) {
            setSnackbar({
                open: true,
                message: "Please fill all required fields",
                severity: "error",
            });
            return;
        }

        try {
            let status = submitAction === 'publish' ? '3' : '1';
            const formData = new FormData();
            
            formData.append("enrollment_type", parseInt(settings.enrollment_type));
            //const selfUnenrollValue = settings.lpath_end_date ? "0" : (settings.self_unenrollment ? "1" : "0");
            //formData.append("self_unenrollment", selfUnenrollValue);
            formData.append("self_unenrollment", settings.self_unenrollment ? "1" : "0");
            
            formData.append("auto_retire", toCalendarDay(settings.auto_retire));
            formData.append("status", status);
            
            // Add authors
            settings.authors.forEach((author) => { 
                formData.append("authors[]", author.id); 
            });

            //formData.append("mandatory_lpath", settings.mandatory_lpath ? "1" : "0");
            //formData.append("lpath_end_date", settings.lpath_end_date ? settings.lpath_end_date.toISOString().split("T")[0] : "");

            // Add resources
            if (settings.files?.length > 0) {
                formData.append("resourse_type", "file");
                settings.files.forEach((file) => {
                  formData.append("files[]", file);
                });
            } else if (settings.resourse_url?.trim()) {
                formData.append("resourse_type", "url");
                const urlObj = [{
                  name: "Resource URL",
                  url: settings.resourse_url.trim(),
                }];
                formData.append("urls", JSON.stringify(urlObj));
            }

            deletedAttachmentIds.forEach((id) => {
                formData.append("deleted_attachments[]", id);
            });

            await learningPathService.updateSetting(id, formData, {
                headers: { "Content-Type": "multipart/form-data" }
            });
            
            showToast('Settings updated successfully', 'success');
            setTimeout(() => {
                navigate(`/learning_path/contents/${id}`);
            }, 1000);
        } catch (error) {
            console.error('Error updating settings:', error);
            // A refused save shows the server's reason, such as which files a resource can be (S-26 pt 1.1).
            showToast((error.response?.status === 400 && error.response.data?.message) || 'Error updating settings', 'error');
        }
    };

    const handlePrerequisiteSearch = async (searchText) => {
        if (searchText.length < 2) return;
        try {
            const response = await learningPathService.searchPrerequisites(id, searchText);
            if (response.data && response.data.aaData) {
                setLPaths(response.data.aaData);
            } else {
                console.error('Invalid response format:', response);
                setLPaths([]);
            }
        } catch (error) {
            console.error('Error searching prerequisites:', error);
            showToast('Error searching prerequisites', 'error');
            setLPaths([]);
        }
    };

    const handleAuthorSearch = async (searchText) => {
        const response = await learningPathService.getAuthors(searchText);
        setAuthorOptions(response.data);
    };

    const handleAuthorsChange = (event, newValue) => {
        const currentUserAuthor = settings.authors.find(
            (author) => author.id === user.id
        );
        setSettings((prev) => ({
            ...prev,
            authors: currentUserAuthor
                ? [
                    currentUserAuthor,
                    ...newValue.filter((author) => author.id !== user.id),
                ]
                : newValue,
        }));
    };

    const filterAuthorOptions = (options) => {
        return options.filter(
            (option) => !settings.authors.some((author) => author.id === option.id)
        );
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        setSettings(prev => ({
            ...prev,
            resourse_type: newValue === 0 ? 'url' : 'file',
            ...(newValue === 0 ? { files: [] } : { resourse_url: '' })
        }));
    };

    const handleChange = (e) => {
        if (e && e.target) {
            const { name, value, type, checked } = e.target;
            setSettings(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        } else if (typeof e === 'object' && e !== null && ('name' in e || 'value' in e)) {
            const { name, value } = e;
            setSettings(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const removeAttachment = async (index) => {
        const attachment = settings.attachments[index];

        if (attachment?.id) {
            try {
                await learningPathService.deleteAttachment(attachment.id);
                setSettings((prev) => ({
                    ...prev,
                    attachments: prev.attachments.filter((_, i) => i !== index),
                }));
                setDeletedAttachmentIds(prev => [...prev, attachment.id]);
            } catch (err) {
                console.error("Failed to delete attachment:", err);
                setSnackbar({
                    open: true,
                    message: "Failed to delete attachment",
                    severity: "error",
                });
            }
        } else {
            setSettings((prev) => ({
                ...prev,
                attachments: prev.attachments.filter((_, i) => i !== index),
            }));
        }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: (acceptedFiles) => {
            setSettings(prev => {
                const existingNames = prev.files?.map(f => f.name) || [];
                const newFiles = acceptedFiles.filter(f => !existingNames.includes(f.name));
                return {
                    ...prev,
                    files: [...(prev.files || []), ...newFiles],
                };
            });
        },
        // The files a resource can be (S-26 pt 1.1): PDF, DOCX or TXT, as the server checks. Removed 17 Oct 2026 (S-26
        // pt 1.1): DOC, PPT, PPTX, XLS, XLSX and ZIP, which the server refuses.
        accept: {
            'application/pdf': ['.pdf'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
            'text/plain': ['.txt'],
        },
        disabled: tabValue !== 1
    });

    const handlePrerequisiteToggle = (id, required) => {
        // setSettings(prev => ({
        //     ...prev,
        //     prerequisites: prev.prerequisites.map(p =>
        //         p.id === id ? { ...p, required } : p
        //     )
        // }));
        
        // prerequisites removed from learning path settings UI
        return;
    };

    const isPrerequisiteAdded = (id) => false;

    const isPastDate = (dateValue) => {
        if (!dateValue) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const selectedDate = new Date(dateValue);
        selectedDate.setHours(0, 0, 0, 0);
        return selectedDate < today;
    };
    /*
    const isInvalidDateRelationship = () => {
        return !!settings.lpath_end_date && !!settings.auto_retire && settings.lpath_end_date > settings.auto_retire;
    };

    const isInvalidAutoRetire = () => {
        return !!settings.auto_retire && !!settings.lpath_end_date && settings.auto_retire < settings.lpath_end_date;
    };
    */
    const getDateFieldSx = (isInvalid) => ({
        opacity: isInvalid ? 0.55 : 1,
        '& .MuiInputBase-input': {
            color: isInvalid ? 'text.disabled' : 'inherit',
            cursor: 'pointer',
        },
    });
    /*
    const handleClearLPathEndDate = () => {
        setSettings(prev => ({
            ...prev,
            lpath_end_date: null,
            self_unenrollment: true
        }));
    };
    */
    const handleClearAutoRetire = () => {
        // Clear the auto_retire date so payload sends null
        setSettings(prev => ({ ...prev, auto_retire: null }));
    };
    return (
        <Box sx={{ p: 2 }}>
            <PageTabs tabs={lpathTabs} />
            <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
                <Typography variant="h5" sx={{ mb: 4 }}>Learning Path Settings</Typography>
                <Grid container spacing={3}>
                    {/* <Grid item xs={12} md={12}>
                        <FormControl fullWidth variant="outlined" error={!!errors.enrollment_type} size="small">
                            <InputLabel shrink id="enrollment-type-label">Enrollment Type</InputLabel>
                            <Select
                                name="enrollment_type"
                                labelId="enrollment-type-label"
                                value={settings.enrollment_type}
                                onChange={handleChange}
                                label="Enrollment Type"
                            >
                                <MenuItem value={2}>Self Enroll</MenuItem>
                                <MenuItem value={1}>Admin Enroll</MenuItem>
                            </Select>
                            {errors.enrollment_type && (
                                <FormHelperText>{errors.enrollment_type}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid> */}

                    {/* <Grid item xs={12} md={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={settings.self_unenrollment}
                                    onChange={(e) =>
                                        setSettings((prev) => ({
                                        ...prev,
                                        self_unenrollment: e.target.checked,
                                        }))
                                    }
                                    name="self_unenrollment"
                                />
                            }
                            disabled={!!settings.lpath_end_date}
                            label={
                                settings.lpath_end_date
                                ? "Allow Self Unenrollment(Disabled when a Compliance Date is set)"
                                : "Allow Self Unenrollment"
                            }
                        />
                    </Grid> */}

                    <Grid item xs={12} md={12}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <DatePicker
                                label="Auto Retire Date"
                                value={settings.auto_retire}
                                onChange={(newValue) => {
                                    setSettings((prev) => ({
                                    ...prev,
                                    auto_retire: newValue,
                                    }))
                                }}
                                disablePast
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        inputProps: { readOnly: true },
                                        sx: getDateFieldSx(false),
                                    },
                                }}
                            />
                            <IconButton
                                aria-label="clear auto retire"
                                onClick={handleClearAutoRetire}
                                size="large"
                                sx={{
                                    bgcolor: 'transparent',
                                    '&:hover': { backgroundColor: 'transparent' },
                                    p: 0,
                                    alignSelf: 'center',
                                    minWidth: 36,
                                    height: 36,
                                    borderRadius: '6px',
                                    '& .MuiSvgIcon-root': {
                                        fontWeight: 700,
                                        fontSize: '20px',
                                    },
                                }}
                            >
                                <ClearIcon />
                            </IconButton>
                        </Box>
                        {/*
                        <Grid item xs={12}>
                            <Typography variant="caption" color="textSecondary">
                                Auto-retire date must be after the compliance date.
                            </Typography>
                        </Grid>
                        */}
                    </Grid>
                    {/*
                    <Grid item xs={12} md={12}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <DatePicker
                                label="Compliance (End Date)"
                                value={settings.lpath_end_date}
                                onChange={(newValue) =>  {
                                    setSettings((prev) => {
                                    const updatedSettings = {
                                        ...prev,
                                        lpath_end_date: newValue,
                                    };
                                    // Condition 1: If End Date is set, turn off self_unenrollment
                                    if (newValue) {
                                        updatedSettings.self_unenrollment = false;
                                        updatedSettings.mandatory_lpath = false; 
                                    }
                                    // If End Date is removed and enrollment is self_enroll, turn on self_unenrollment
                                    else {
                                        updatedSettings.self_unenrollment = true;
                                    }

                                    return updatedSettings;
                                });
                            }}
                                disablePast
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        inputProps: { readOnly: true },
                                        sx: getDateFieldSx(isInvalidDateRelationship()),
                                    },
                                }}
                                maxDate={settings.auto_retire || null}
                            />
                            <IconButton
                                aria-label="clear end date"
                                onClick={handleClearLPathEndDate}
                                size="large"
                                sx={{
                                    bgcolor: 'transparent',
                                    '&:hover': { backgroundColor: 'transparent' },
                                    p: 0,
                                    alignSelf: 'center',
                                    minWidth: 36,
                                    height: 36,
                                    borderRadius: '6px',
                                    '& .MuiSvgIcon-root': {
                                        fontWeight: 700,
                                        fontSize: '20px',
                                    },
                                }}
                            >
                                <ClearIcon />
                            </IconButton>
                        </Box>
                        <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                {settings.lpath_end_date
                                ? 'Self Enrollment and Required learning path options are disabled when Compliance is enabled.'
                                : 'Learners must complete the learning path by this date.'}
                            </Typography>
                        </Grid>
                    </Grid>
                    */}
                    {/*
                    <Grid item xs={12} md={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={settings.mandatory_lpath}
                                    onChange={(e) =>
                                        setSettings((prev) => ({
                                            ...prev,
                                            mandatory_lpath: e.target.checked,
                                        }))
                                    }
                                    name="mandatory_lpath"
                                />
                            }
                            disabled={!!settings.lpath_end_date}
                            label={
                            settings.lpath_end_date
                                ? "Required Learning Path(Disabled when a compliance date is set)"
                                : "Required Learning Path"
                            }
                        />
                    </Grid>
                    */}    

                {/*<Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>Prerequisites</Typography>
                    <Box sx={{ mb: 2 }}>
                        <Autocomplete
                            options={lpaths.filter(lpath => !isPrerequisiteAdded(lpath[0]))}
                            getOptionLabel={(option) => option[1]}
                            value={null}
                            onChange={(event, newValue) => {
                                if (newValue) {
                                    setSettings(prev => ({
                                        ...prev,
                                        prerequisites: [...prev.prerequisites, {
                                            id: newValue[0],
                                            name: newValue[1],
                                            prerequisite_type: newValue[2] || 'learning path',
                                            // required: false
                                            required:true
                                        }]
                                    }));
                                }
                            }}
                            onInputChange={(event, value) => {
                                handlePrerequisiteSearch(value);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Prerequisites"
                                    value=""
                                />
                            )}
                        />
                    </Box>
                    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
                        <SortableContext
                            items={settings.prerequisites.map(p => String(p.oid))}
                            strategy={verticalListSortingStrategy}
                        >
                            {settings.prerequisites.map((prerequisite) => (
                                <LPathSortablePrerequisite
                                    key={prerequisite.oid}
                                    id={String(prerequisite.oid)}
                                    prerequisite={prerequisite}
                                    onToggle={handlePrerequisiteToggle}
                                    onRemove={handleRemovePrerequisite}
                                />
                            ))}
                        </SortableContext>
                    </DndContext>
                </Grid>
                */}
                <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                        Learning Path Authors
                    </Typography>
                    <Autocomplete
                        multiple
                        options={filterAuthorOptions(authorOptions)}
                        getOptionLabel={(option) =>
                            `${option.first_name} ${option.last_name}`
                        }
                        value={settings.authors || []}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        onInputChange={(event, value) => {
                            handleAuthorSearch(value);
                        }}
                        onChange={handleAuthorsChange}
                        renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                                {`${option.first_name} ${option.last_name}`}
                            </li>
                        )}
                        renderTags={(tagValue, getTagProps) =>
                            tagValue.map((option, index) => {
                                const { key, ...tagProps } = getTagProps({ index });
                                return (
                                    <Typography
                                        key={key}
                                        variant="body2"
                                        sx={{
                                            display: "inline-block",
                                            backgroundColor: "#f0f0f0",
                                            padding: "6px 12px",
                                            marginRight: "8px",
                                            marginBottom: "8px",
                                            borderRadius: "4px",
                                            fontWeight: "500",
                                        }}
                                        {...tagProps}
                                    >
                                        {`${option.first_name} ${option.last_name}`}
                                    </Typography>
                                );
                            })
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Authors"
                                    error={!!errors.authors}
                                    helperText={errors.authors}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            bgcolor: "white",
                                        },
                                    }}
                                />
                            )}
                            ChipProps={{ style: { display: "none" } }}
                            sx={{
                                "& .MuiChip-root": {
                                    display: "none",
                                },
                            }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            Resources
                        </Typography>
                        {Array.isArray(settings.attachments) && settings.attachments.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Current Resources
                                </Typography>
                                <Stack spacing={1}>
                                    {settings.attachments.map((attachment, index) => (
                                        <Card
                                            key={index}
                                            variant="outlined"
                                            sx={{ display: "flex", alignItems: "center" }}
                                        >
                                            <CardContent sx={{ flexGrow: 1, py: 1 }}>
                                                <Typography variant="body2">
                                                    {attachment.name || attachment.filename || "Attachment"}
                                                </Typography>
                                            </CardContent>
                                            <IconButton
                                                color="error"
                                                onClick={() => removeAttachment(index)}
                                                sx={{ mr: 1 }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Card>
                                    ))}
                                </Stack>
                            </Box>
                        )}
                        {(settings.files?.length > 0 || settings.resourse_url?.trim()) && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    New Resources
                                </Typography>
                                <Stack spacing={1}>
                                    {settings.files?.map((file, index) => (
                                        <Card
                                            key={index}
                                            variant="outlined"
                                            sx={{ display: "flex", alignItems: "center" }}
                                        >
                                            <CardContent sx={{ flexGrow: 1, py: 1 }}>
                                                <Typography variant="body2">{file.name}</Typography>
                                            </CardContent>
                                            <IconButton
                                                color="error"
                                                onClick={() =>
                                                    setSettings(prev => ({
                                                        ...prev,
                                                        files: prev.files.filter((_, i) => i !== index)
                                                    }))
                                                }
                                                sx={{ mr: 1 }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Card>
                                    ))}
                                    {tabValue === 0 && settings.resourse_url?.trim() && (
                                        <Card variant="outlined" sx={{ display: "flex", alignItems: "center" }}>
                                            <CardContent sx={{ flexGrow: 1, py: 1 }}>
                                                <Typography variant="body2">{settings.resourse_url}</Typography>
                                            </CardContent>
                                            <IconButton
                                                color="error"
                                                onClick={() =>
                                                    setSettings(prev => ({ ...prev, resourse_url: '' }))
                                                }
                                                sx={{ mr: 1 }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Card>
                                    )}
                                </Stack>
                            </Box>
                        )}
                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            aria-label="content type tabs"
                            sx={{ mb: 3 }}
                        >
                            <Tab icon={<LinkIcon />} label="URL" />
                            <Tab icon={<UploadIcon />} label="File Upload" />
                        </Tabs>

                        {tabValue === 0 ? (
                            <TextField
                                fullWidth
                                label="Resource URL"
                                name="resourse_url"
                                value={settings.resourse_url || ''}
                                onChange={handleChange}
                                variant="outlined"
                                placeholder="https://example.com/content"
                            />
                        ) : (
                            <DropzoneArea {...getRootProps()}>
                                <input {...getInputProps()} />
                                <UploadIcon sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />
                                <Typography variant="body1" sx={{ mb: 1 }}>
                                    {isDragActive ? "Drop files here" : "Drag and drop files here"}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    or click to browse
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ mt: 2, display: "block" }}
                                >
                                    Supported formats: PDF, DOCX or TXT, up to 50 MB
                                </Typography>
                            </DropzoneArea>
                        )}
                    </Grid>
                </Grid>



                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={() => handleSubmit('save')}
                        disabled={loading}
                    >
                        Save
                    </Button>
                    {/* <Button 
                        variant="contained"
                        onClick={() => handleSubmit('publish')}
                        disabled={loading}
                    >
                        {settings.status === 3 ? 'Republish' : 'Publish'}
                    </Button> */}
                </Box>
            </Box>
        </Box>
    );
};

export default LPathSettings;